import dayjs from 'dayjs'
import { IChatParticipant, IChatResponse, IUserSession } from 'types'
import { IPatientResponse } from 'types/patient/types'

/**
 * ChatMessage class represents a message in a chat.
 * It implements the IChatResponse interface and provides methods to handle message data.
 */
export class ChatMessage implements IChatResponse {
  /** The unique identifier for the chat. */
  chat_id: string = ''
  /** The date and time when the message was created. */
  created_at: string = ''
  /** The content of the message. */
  message_content: string = ''
  /** The unique identifier for the message. */
  message_id: string = ''
  /** The unique identifier for the sender of the message. */
  sender_id: string = ''

  constructor(data: IChatResponse) {
    for (const index in data) {
      const key = index as keyof IChatResponse
      this[key] = data[key]
    }
  }

  /**
   * Gets the date of the message in a human-readable format.
   * If the message was created today, it returns 'Hoje'.
   * Otherwise, it returns the date in 'DD/MM' format.
   */
  get date(): string {
    const today = dayjs().format('DD/MM/YY')

    if (today == dayjs(this.created_at).format('DD/MM/YY')) {
      return 'Hoje'
    }

    return dayjs(this.created_at).format('DD/MM')
  }

  /**
   * Gets the time of the message in 'HH:mm' format.
   */
  get messageTime(): string {
    return dayjs(this.created_at).format('HH:mm')
  }

  /**
   * Checks if two chat messages are equal.
   */
  equals = (other: object) => {
    if ('chat_id' in other && 'message_id' in other && 'sender_id' in other) {
      return this.message_id === other.message_id
    }
    return false
  }
}

const _mountFullName = (participant: IChatParticipant): string => {
  return [participant.first_name.trim(), participant.last_name.trim()].join(' ')
}

/**
 * ChatParticipant class represents a participant in a chat.
 */
export class ChatParticipant {
  /**
   * The unique identifier for the chat.
   */
  chat_id: string = ''
  /**
   * The list of participants in the chat.
   */
  participantList: IChatParticipant[]

  constructor(key: string, participantList: IChatParticipant[]) {
    this.chat_id = key
    this.participantList = participantList
  }

  /**
   * Creates a ChatParticipant instance from an IPatientResponse object.
   */
  static fromPatientInterface(patient: IPatientResponse): ChatParticipant {
    return new ChatParticipant(patient?.chat_id ?? '', [
      {
        ...patient,
        participant_id: patient?.patient_id,
        user_type: 'patient'
      }
    ])
  }

  /**
   * Gets the full name of all participants in the chat.
   * If there are multiple participants, their names are concatenated.
   */
  get fullName(): string {
    return this.participantList
      .slice(1)
      .map(item => _mountFullName(item))
      .reduce(
        (a, b) => `${a}, ${b}`,
        this.participantList.length > 0
          ? _mountFullName(this.participantList[0])
          : ''
      )
  }

  /**
   * Gets the image of the participant if there is only one participant in the chat.
   */
  get image(): string | undefined {
    if (this.participantList.length == 1) {
      return this.participantList[0].user_image
    }
    return undefined
  }

  /**
   * Checks if all participants have requested deletion.
   */
  get requested_deletion(): boolean {
    return this.participantList
      .map(item => item.requested_deletion)
      .reduce((a, b) => a && b, true)
  }

  /**
   * Converts the participant list to an array of IPatientResponse or IUserSession objects.
   * If the user type is 'patient', it returns an IPatientResponse object.
   * If the user type is not 'patient', it returns an IUserSession object.
   */
  get convertToInterfaceType(): Array<IPatientResponse | IUserSession> {
    return this.participantList.map(participant => {
      const { participant_id, user_type, ...data } = participant
      if (user_type == 'patient') {
        return {
          ...data,
          patient_id: participant_id,
          chat_id: this.chat_id
        } as IPatientResponse
      }
      return {
        ...data,
        professional_id: participant_id,
        chat_id: this.chat_id,
        treatment: ''
      } as IUserSession
    })
  }
}
